import React from 'react';
import Chip from '@material-ui/core/Chip';
import Skeleton from '@material-ui/lab/Skeleton';
import isNil from 'lodash/isNil';
import { AllFiltersDefinition } from 'UI/components/templates/SideFiltersLayout/filters';
import { flattenFilters } from 'UI/utils/filters';

import { useStyles } from './styles';

/** @typedef {import('types/app').Filters} Filters */
/** @typedef {import('UI/components/templates/SideFiltersLayout/filters').FilterDefinition} FilterDefinition */

const getDisplayLabel = ({ entityTitle, extraFiltersConfig = [], filter }) => {
  if (!filter || isNil(filter.value)) return '';

  const extraFilterNames = extraFiltersConfig.map(filterItem => filterItem.name);
  const cleanedFilters = AllFiltersDefinition.filter(
    filterConfig => !extraFilterNames.includes(filterConfig.name)
  );

  const finalFilters = [...cleanedFilters, ...extraFiltersConfig];

  const filterDef = finalFilters.find(({ name }) => name === filter.id);

  const renderer = filterDef?.getOptionLabel;
  const title =
    (renderer && renderer(filter.value, { entityTitle })) ||
    filter.value.compound_title ||
    filter.value.title ||
    filter.value.full_name ||
    filter.value.name ||
    filter.value;

  const displayLabelPrefix = filter.displayLabel ? `${filter.displayLabel}: ` : '';
  return displayLabelPrefix + title;
};

/**
 * @param {Object} props
 * @param {string} [props.entityTitle='']
 * @param {FilterDefinition[]} [props.extraFiltersConfig]
 * @param {Filters} props.filters
 * @param {boolean} props.isLoading
 * @param {Function} [props.onFilterRemove]
 * @param {Function} props.onReset
 * @param {boolean} [props.shouldDisplayLabel=true]
 */
const ActiveFilters = ({
  entityTitle = '',
  extraFiltersConfig,
  filters,
  isLoading,
  onFilterRemove,
  onReset,
  shouldDisplayLabel = true
}) => {
  const classes = useStyles();

  const handleDeleteClick = (filterId, value) => {
    onFilterRemove && onFilterRemove(filterId, value);
  };

  const activeFilters = flattenFilters(filters);

  const filtersSkeleton = numberOfFilters => {
    return Array.from({ length: numberOfFilters + 1 })
      .map((_, index) => index)
      .map(index => <Skeleton key={`filter-${index}`} width="10%" height={32} radius={16} />);
  };

  return (
    activeFilters.length > 0 && (
      <div className={classes.root}>
        {isLoading && filtersSkeleton(activeFilters.length)}
        {!isLoading && (
          <>
            {shouldDisplayLabel && <b>Filters: </b>}
            {activeFilters.map(filter => {
              const filterValue = filter.value?.id || filter.value;
              return (
                <Chip
                  key={`${filter.id}-${filterValue}`}
                  label={getDisplayLabel({ filter, extraFiltersConfig, entityTitle })}
                  onDelete={() => {
                    handleDeleteClick(filter.idKey ?? filter.id, filterValue);
                  }}
                  color="primary"
                  className={classes.chip}
                />
              );
            })}
            {activeFilters.length > 1 && (
              <Chip label="Reset all filters" onDelete={onReset} className={classes.chip} />
            )}
          </>
        )}
      </div>
    )
  );
};

export default ActiveFilters;
